import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setUser, clearUser, trackAction } from '../lib/sentry';
import { config } from '../config';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const API_URL = config.apiUrl;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await fetch(`${API_URL}/api/v1/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Login failed' }));
          throw new Error(error.error || error.message || 'Login failed');
        }

        const data = await response.json();
        set({
          user: data.user,
          tenant: data.tenant,
          token: data.token,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
        });

        // Set Sentry user context
        if (data.user) {
          setUser(data.user);
          trackAction('user_login', { userId: data.user.id, tenantId: data.tenant?.id });
        }
      },

      logout: () => {
        trackAction('user_logout');
        clearUser();
        set({
          user: null,
          tenant: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'wpcruddly-auth',
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
